import { useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  NOTIFICATION_CHANNELS,
  NOTIFICATION_DELIVERY_STATUSES,
  NotificationDeliverySchema,
  NotificationEventSchema,
  NotificationRuleSchema,
  RoleSummarySchema,
  type NotificationChannel,
  type NotificationEventDto,
  type NotificationRuleDto,
} from '@taqon/contracts';
import { z } from 'zod';
import { getData, getPage, patchJson, postJson } from '../../shared/api/client';
import { hasCapability, useSession } from '../../shared/session/session';
import { ResendEmailButton } from '../integration/ResendEmailButton';
import { FormDialog, StatusChip } from '../../shared/ui/forms';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { PersonName } from '../../shared/ui/person';
import { RecordLink } from '../finance/detail';
import { SearchFilterBar } from '../../shared/ui/filters';

const RULES = '/api/v1/platform/notification-rules';
const EVENTS = '/api/v1/platform/notification-events';
const DELIVERIES = '/api/v1/platform/notifications';
const RULES_KEY = ['platform', 'notification-rules'] as const;
const PAGE_SIZES = [25, 50, 100];

const CHANNEL_LABELS: Readonly<Record<NotificationChannel, string>> = {
  in_app: 'In-system',
  email: 'Email',
};

/** `{{name}}` placeholders a template uses, the same rule the API applies. */
export function placeholdersIn(template: string): string[] {
  const names: string[] = [];
  for (const match of template.matchAll(/\{\{\s*([a-zA-Z][a-zA-Z0-9_]*)\s*\}\}/g)) {
    const name = match[1];
    if (name !== undefined && !names.includes(name)) names.push(name);
  }
  return names;
}

function useEvents() {
  return useQuery({
    queryKey: ['platform', 'notification-events'],
    queryFn: () => getData(EVENTS, z.array(NotificationEventSchema)),
  });
}

/**
 * Notification rules (FRD-CORE-027): which declared event notifies whom, on which channels and
 * with which wording, configured without a release; and the delivery log with failure reasons
 * (FRD-INT-011). Privileged configuration: every change is audited by the API.
 */
export function NotificationRulesPage() {
  const [tab, setTab] = useState(0);
  return (
    <Stack spacing={3}>
      <PageHeader
        title="Notification rules"
        description="Who is notified of each event, on which channels, and with which wording."
      />
      <Tabs
        value={tab}
        onChange={(_, value: number) => {
          setTab(value);
        }}
        aria-label="Notification administration"
      >
        <Tab label="Rules" />
        <Tab label="Delivery log" />
      </Tabs>
      {tab === 0 ? <RulesTab /> : <DeliveryLogTab />}
    </Stack>
  );
}

function RulesTab() {
  const session = useSession();
  const canCreate = hasCapability(session.data, 'platform.notification_rule:create');
  const canChange = hasCapability(session.data, 'platform.notification_rule:change');
  const events = useEvents();
  const [eventType, setEventType] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [editing, setEditing] = useState<NotificationRuleDto | 'new' | null>(null);
  const [toggling, setToggling] = useState<NotificationRuleDto | null>(null);

  const params = new URLSearchParams({ page: String(page + 1), pageSize: String(pageSize) });
  if (eventType !== '') params.set('filter[event_type]', eventType);
  if (status !== '') params.set('filter[status]', status);
  const rules = useQuery({
    queryKey: [...RULES_KEY, eventType, status, page, pageSize],
    queryFn: () => getPage(`${RULES}?${params.toString()}`, NotificationRuleSchema),
    placeholderData: keepPreviousData,
  });

  return (
    <>
      <SearchFilterBar aria-label="Rule filters">
        <TextField
          select
          size="small"
          label="Event"
          value={eventType}
          onChange={(e) => {
            setEventType(e.target.value);
            setPage(0);
          }}
          sx={{ minWidth: 240 }}
        >
          <MenuItem value="">All events</MenuItem>
          {(events.data ?? []).map((event) => (
            <MenuItem key={event.eventType} value={event.eventType}>
              {event.eventType}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Status"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(0);
          }}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">Any status</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="inactive">Inactive</MenuItem>
        </TextField>
      </SearchFilterBar>
      <Section
        title="Rules"
        actions={
          canCreate ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditing('new');
              }}
            >
              New rule
            </Button>
          ) : undefined
        }
      >
        {events.isError && <ErrorState message={events.error.message} />}
        {rules.isError && <ErrorState message={rules.error.message} />}
        {rules.isPending && <LoadingState label="Loading notification rules" />}
        {rules.data?.items.length === 0 && (
          <EmptyState
            title="No notification rules"
            detail="Without an active rule, an event notifies nobody."
          />
        )}
        {rules.data !== undefined && rules.data.items.length > 0 && (
          <>
            <Table size="small" aria-label="Notification rules">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Event</TableCell>
                  <TableCell>Parties</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Channels</TableCell>
                  <TableCell>Status</TableCell>
                  {canChange && <TableCell align="right">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {rules.data.items.map((rule) => (
                  <TableRow key={rule.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{rule.name}</TableCell>
                    <TableCell>{rule.eventType}</TableCell>
                    <TableCell>
                      {rule.parties.length === 0 ? '—' : rule.parties.join(', ')}
                    </TableCell>
                    <TableCell>{rule.roleName ?? '—'}</TableCell>
                    <TableCell>{rule.channels.map((c) => CHANNEL_LABELS[c]).join(', ')}</TableCell>
                    <TableCell>
                      <StatusChip status={rule.status} />
                    </TableCell>
                    {canChange && (
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Button
                          size="small"
                          onClick={() => {
                            setEditing(rule);
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          size="small"
                          color={rule.status === 'active' ? 'warning' : 'primary'}
                          onClick={() => {
                            setToggling(rule);
                          }}
                        >
                          {rule.status === 'active' ? 'Deactivate' : 'Activate'}
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={rules.data.total}
              page={page}
              rowsPerPage={pageSize}
              rowsPerPageOptions={PAGE_SIZES}
              onPageChange={(_, next) => {
                setPage(next);
              }}
              onRowsPerPageChange={(event) => {
                setPageSize(Number(event.target.value));
                setPage(0);
              }}
            />
          </>
        )}
      </Section>
      {editing !== null && (
        <RuleDialog
          key={editing === 'new' ? 'new' : `${editing.id}:${String(editing.version)}`}
          rule={editing === 'new' ? null : editing}
          events={events.data ?? []}
          onClose={() => {
            setEditing(null);
          }}
        />
      )}
      {toggling !== null && (
        <StatusDialog
          rule={toggling}
          onClose={() => {
            setToggling(null);
          }}
        />
      )}
    </>
  );
}

/** Activation is consequential: an inactive rule notifies nobody. Confirmed, then sent with If-Match. */
function StatusDialog({ rule, onClose }: { rule: NotificationRuleDto; onClose: () => void }) {
  const session = useSession();
  const queryClient = useQueryClient();
  const next = rule.status === 'active' ? 'inactive' : 'active';
  const change = useMutation({
    mutationFn: () =>
      patchJson(
        `${RULES}/${rule.id}`,
        { status: next },
        NotificationRuleSchema,
        session.data?.csrfToken ?? '',
        rule.version,
      ),
    onSuccess: async () => {
      onClose();
      await queryClient.invalidateQueries({ queryKey: RULES_KEY });
    },
  });
  return (
    <FormDialog
      open
      title={`${next === 'active' ? 'Activate' : 'Deactivate'} ${rule.name}`}
      submitLabel={next === 'active' ? 'Activate' : 'Deactivate'}
      canSubmit
      submitting={change.isPending}
      error={change.isError ? change.error.message : null}
      onClose={onClose}
      onSubmit={() => {
        change.mutate();
      }}
    >
      <Typography variant="body2">
        {next === 'active'
          ? `From now on, ${rule.eventType} notifies the recipients of this rule.`
          : `From now on, this rule no longer notifies anyone of ${rule.eventType}. Notifications already sent are kept.`}
      </Typography>
    </FormDialog>
  );
}

/**
 * Create or edit a rule. The event decides which parties and placeholders exist; a template may
 * use only the event's placeholders, which the chips insert. The API validates the same things.
 */
export function RuleDialog({
  rule,
  events,
  onClose,
}: {
  rule: NotificationRuleDto | null;
  events: readonly NotificationEventDto[];
  onClose: () => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const canPickRole = hasCapability(session.data, 'platform.role:view');
  const [eventType, setEventType] = useState(rule?.eventType ?? '');
  const [name, setName] = useState(rule?.name ?? '');
  const [parties, setParties] = useState<readonly string[]>(rule?.parties ?? []);
  const [roleId, setRoleId] = useState(rule?.roleId ?? '');
  const [channels, setChannels] = useState<readonly NotificationChannel[]>(
    rule?.channels ?? ['in_app'],
  );
  const [subjectTemplate, setSubjectTemplate] = useState(rule?.subjectTemplate ?? '');
  const [bodyTemplate, setBodyTemplate] = useState(rule?.bodyTemplate ?? '');
  const [target, setTarget] = useState<'subject' | 'body'>('body');
  const subjectRef = useRef<HTMLInputElement | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);

  const roles = useQuery({
    queryKey: ['platform', 'roles'],
    enabled: canPickRole,
    queryFn: () => getData('/api/v1/platform/roles', z.array(RoleSummarySchema)),
  });
  const event = events.find((e) => e.eventType === eventType);
  const declared = event?.placeholders ?? [];
  const unknownIn = (template: string) =>
    placeholdersIn(template).filter((p) => !declared.includes(p));
  const subjectUnknown = event === undefined ? [] : unknownIn(subjectTemplate);
  const bodyUnknown = event === undefined ? [] : unknownIn(bodyTemplate);
  const noRecipients = parties.length === 0 && roleId === '';

  const body = {
    eventType,
    name: name.trim(),
    parties,
    roleId: roleId === '' ? null : roleId,
    channels,
    subjectTemplate: subjectTemplate.trim(),
    bodyTemplate: bodyTemplate.trim(),
  };
  const save = useMutation({
    mutationFn: () =>
      rule === null
        ? postJson(RULES, body, NotificationRuleSchema, session.data?.csrfToken ?? '')
        : patchJson(
            `${RULES}/${rule.id}`,
            body,
            NotificationRuleSchema,
            session.data?.csrfToken ?? '',
            rule.version,
          ),
    onSuccess: async () => {
      onClose();
      await queryClient.invalidateQueries({ queryKey: RULES_KEY });
    },
  });

  const insert = (placeholder: string) => {
    const token = `{{${placeholder}}}`;
    const element = target === 'subject' ? subjectRef.current : bodyRef.current;
    const value = target === 'subject' ? subjectTemplate : bodyTemplate;
    const start = element?.selectionStart ?? value.length;
    const end = element?.selectionEnd ?? value.length;
    const next = `${value.slice(0, start)}${token}${value.slice(end)}`;
    if (target === 'subject') setSubjectTemplate(next);
    else setBodyTemplate(next);
  };
  const toggle = <T extends string>(list: readonly T[], value: T): T[] =>
    list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

  const canSubmit =
    event !== undefined &&
    body.name !== '' &&
    channels.length > 0 &&
    body.subjectTemplate !== '' &&
    body.bodyTemplate !== '' &&
    !noRecipients &&
    subjectUnknown.length === 0 &&
    bodyUnknown.length === 0;
  const selectableRoles = (roles.data ?? []).filter(
    (role) => role.status === 'active' || role.id === rule?.roleId,
  );

  return (
    <FormDialog
      open
      title={rule === null ? 'New notification rule' : `Edit ${rule.name}`}
      submitLabel={rule === null ? 'Create rule' : 'Save changes'}
      canSubmit={canSubmit}
      submitting={save.isPending}
      error={save.isError ? save.error.message : null}
      onClose={onClose}
      onSubmit={() => {
        save.mutate();
      }}
    >
      <TextField
        select
        label="Event"
        required
        value={eventType}
        onChange={(e) => {
          setEventType(e.target.value);
          const next = events.find((ev) => ev.eventType === e.target.value);
          setParties((current) => current.filter((p) => next?.parties.includes(p) ?? false));
        }}
        helperText={
          events.length === 0
            ? 'No module has declared a notification event yet.'
            : event?.description
        }
      >
        {events.map((e) => (
          <MenuItem key={e.eventType} value={e.eventType}>
            {e.eventType}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        label="Name"
        required
        value={name}
        onChange={(e) => {
          setName(e.target.value);
        }}
      />
      {event !== undefined && (
        <FormControl component="fieldset" error={noRecipients}>
          <FormLabel component="legend">Notify these parties of the event</FormLabel>
          <FormGroup row>
            {event.parties.map((party) => (
              <FormControlLabel
                key={party}
                label={party}
                control={
                  <Checkbox
                    checked={parties.includes(party)}
                    onChange={() => {
                      setParties((current) => toggle(current, party));
                    }}
                  />
                }
              />
            ))}
          </FormGroup>
          {event.parties.length === 0 && (
            <FormHelperText>This event names no parties: choose a role.</FormHelperText>
          )}
        </FormControl>
      )}
      {canPickRole ? (
        <TextField
          select
          label="Also notify the holders of a role"
          value={roleId}
          onChange={(e) => {
            setRoleId(e.target.value);
          }}
          error={noRecipients && event !== undefined}
          helperText={
            noRecipients && event !== undefined
              ? 'Choose at least one party or a role.'
              : 'Everyone currently assigned the role, in any scope.'
          }
        >
          <MenuItem value="">No role</MenuItem>
          {selectableRoles.map((role) => (
            <MenuItem key={role.id} value={role.id}>
              {role.name}
            </MenuItem>
          ))}
        </TextField>
      ) : (
        rule?.roleName !== null &&
        rule?.roleName !== undefined && (
          <Typography variant="body2">Role holders notified: {rule.roleName}</Typography>
        )
      )}
      <FormControl component="fieldset" error={channels.length === 0}>
        <FormLabel component="legend">Channels</FormLabel>
        <FormGroup row>
          {NOTIFICATION_CHANNELS.map((channel) => (
            <FormControlLabel
              key={channel}
              label={CHANNEL_LABELS[channel]}
              control={
                <Checkbox
                  checked={channels.includes(channel)}
                  onChange={() => {
                    setChannels((current) => toggle(current, channel));
                  }}
                />
              }
            />
          ))}
        </FormGroup>
        {channels.length === 0 && <FormHelperText>Choose at least one channel.</FormHelperText>}
      </FormControl>
      <TextField
        label="Subject"
        required
        value={subjectTemplate}
        inputRef={subjectRef}
        onFocus={() => {
          setTarget('subject');
        }}
        onChange={(e) => {
          setSubjectTemplate(e.target.value);
        }}
        error={subjectUnknown.length > 0}
        helperText={
          subjectUnknown.length > 0
            ? `Not a placeholder of this event: ${subjectUnknown.join(', ')}`
            : undefined
        }
      />
      <TextField
        label="Message"
        required
        multiline
        minRows={3}
        value={bodyTemplate}
        inputRef={bodyRef}
        onFocus={() => {
          setTarget('body');
        }}
        onChange={(e) => {
          setBodyTemplate(e.target.value);
        }}
        error={bodyUnknown.length > 0}
        helperText={
          bodyUnknown.length > 0
            ? `Not a placeholder of this event: ${bodyUnknown.join(', ')}`
            : undefined
        }
      />
      {event !== undefined && declared.length > 0 && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Insert a placeholder into the {target === 'subject' ? 'subject' : 'message'}. Sensitive
            values are always masked.
          </Typography>
          <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
            {declared.map((placeholder) => (
              <Chip
                key={placeholder}
                label={`{{${placeholder}}}`}
                size="small"
                variant="outlined"
                onClick={() => {
                  insert(placeholder);
                }}
              />
            ))}
          </Stack>
        </Box>
      )}
    </FormDialog>
  );
}

/** Every delivery with its status and failure reason (FRD-INT-011); never a message body. */
function DeliveryLogTab() {
  const session = useSession();
  const canSeeUsers = hasCapability(session.data, 'platform.app_user:view');
  const timeZone = session.data?.businessTimezone ?? 'Asia/Dubai';
  const events = useEvents();
  const [status, setStatus] = useState('');
  const [channel, setChannel] = useState('');
  const [eventType, setEventType] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const params = new URLSearchParams({ page: String(page + 1), pageSize: String(pageSize) });
  if (status !== '') params.set('filter[delivery_status]', status);
  if (channel !== '') params.set('filter[channel]', channel);
  if (eventType !== '') params.set('filter[event_type]', eventType);
  const deliveries = useQuery({
    queryKey: ['platform', 'notification-deliveries', status, channel, eventType, page, pageSize],
    queryFn: () => getPage(`${DELIVERIES}?${params.toString()}`, NotificationDeliverySchema),
    placeholderData: keepPreviousData,
  });
  const when = (iso: string) =>
    new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeStyle: 'short', timeZone }).format(
      new Date(iso),
    );

  return (
    <>
      <SearchFilterBar aria-label="Delivery log filters">
        <TextField
          select
          size="small"
          label="Status"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(0);
          }}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">Any status</MenuItem>
          {NOTIFICATION_DELIVERY_STATUSES.map((s) => (
            <MenuItem key={s} value={s}>
              {s}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Channel"
          value={channel}
          onChange={(e) => {
            setChannel(e.target.value);
            setPage(0);
          }}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">Any channel</MenuItem>
          {NOTIFICATION_CHANNELS.map((c) => (
            <MenuItem key={c} value={c}>
              {CHANNEL_LABELS[c]}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Event"
          value={eventType}
          onChange={(e) => {
            setEventType(e.target.value);
            setPage(0);
          }}
          sx={{ minWidth: 240 }}
        >
          <MenuItem value="">All events</MenuItem>
          {(events.data ?? []).map((event) => (
            <MenuItem key={event.eventType} value={event.eventType}>
              {event.eventType}
            </MenuItem>
          ))}
        </TextField>
      </SearchFilterBar>
      <Section>
        {deliveries.isError && <ErrorState message={deliveries.error.message} />}
        {deliveries.isPending && <LoadingState label="Loading the delivery log" />}
        {deliveries.data?.items.length === 0 && <EmptyState title="No deliveries match" />}
        {deliveries.data !== undefined && deliveries.data.items.length > 0 && (
          <>
            <Table size="small" aria-label="Delivery log">
              <TableHead>
                <TableRow>
                  <TableCell>Created</TableCell>
                  <TableCell>Event</TableCell>
                  <TableCell>Recipient</TableCell>
                  <TableCell>Channel</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Failure reason</TableCell>
                  <TableCell align="right">Attempts</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {deliveries.data.items.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{when(d.createdAt)}</TableCell>
                    <TableCell>{d.eventType}</TableCell>
                    <TableCell>
                      <PersonName name={d.recipientName} userId={d.recipientUserId}>
                        {canSeeUsers ? (
                          <RecordLink to={`/admin/users/${d.recipientUserId}`}>
                            {d.recipientName}
                          </RecordLink>
                        ) : (
                          d.recipientName
                        )}
                      </PersonName>
                    </TableCell>
                    <TableCell>{CHANNEL_LABELS[d.channel]}</TableCell>
                    <TableCell>{d.title}</TableCell>
                    <TableCell>
                      <StatusChip status={d.deliveryStatus} />
                    </TableCell>
                    <TableCell>{d.failureReason ?? '—'}</TableCell>
                    <TableCell align="right">{d.attempts}</TableCell>
                    <TableCell align="right">
                      <ResendEmailButton delivery={d} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={deliveries.data.total}
              page={page}
              rowsPerPage={pageSize}
              rowsPerPageOptions={PAGE_SIZES}
              onPageChange={(_, next) => {
                setPage(next);
              }}
              onRowsPerPageChange={(event) => {
                setPageSize(Number(event.target.value));
                setPage(0);
              }}
            />
          </>
        )}
      </Section>
    </>
  );
}
